/**
 * ColourSlotPicker (Plan 11, Task 3).
 *
 * Slot-not-name discipline (FND-04): each member gets a PALETTE SLOT,
 * not a colour.  A slot already claimed by a prior member in this
 * family cannot be reselected — the picker renders taken slots
 * `opacity-40` + `aria-disabled="true"` and ignores clicks.
 *
 * The six slots map 1:1 to the `m-terracotta / m-berry / m-ochre /
 * m-sage / m-sky / m-plum` Tailwind v4 `@theme` tokens declared in
 * apps/web/src/styles/theme.css.
 *
 * #159 steg 9f: 44 px sirkler som i Oppsett-skissen, valgt får en ring i
 * `ink` med lerretet som mellomrom. Hver knapp har fargens navn som
 * tilgjengelig navn, så fargen aldri er eneste kjennetegn.
 */
import { useTranslation } from 'react-i18next';
import { COLOR_SLOTS, type ColorSlot } from '@stell/shared';
import { SectionLabel } from '../../../components/primitives/SectionLabel';

export interface ColourSlotPickerProps {
  value: ColorSlot | null;
  onChange: (next: ColorSlot) => void;
  takenSlots: ColorSlot[];
}

const SLOT_CLASS: Record<ColorSlot, string> = {
  'm-terracotta': 'bg-m-terracotta',
  'm-berry': 'bg-m-berry',
  'm-ochre': 'bg-m-ochre',
  'm-sage': 'bg-m-sage',
  'm-sky': 'bg-m-sky',
  'm-plum': 'bg-m-plum',
};

export function ColourSlotPicker({
  value,
  onChange,
  takenSlots,
}: ColourSlotPickerProps) {
  const { t } = useTranslation();
  return (
    <fieldset data-testid="colour-picker" className="m-0 flex flex-col gap-2 border-0 p-0">
      <SectionLabel as="legend">{t('onboarding.step3.colour')}</SectionLabel>
      <div className="flex flex-wrap gap-3">
        {COLOR_SLOTS.map((slot) => {
          const taken = takenSlots.includes(slot) && slot !== value;
          const selected = value === slot;
          const cls = [
            'h-11 w-11 rounded-full transition',
            SLOT_CLASS[slot],
            taken ? 'opacity-40 cursor-not-allowed' : '',
            selected ? 'ring-ink ring-offset-bg ring-2 ring-offset-[3px]' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={slot}
              type="button"
              data-testid={`colour-slot-${slot}`}
              data-taken={taken}
              data-selected={selected}
              aria-disabled={taken}
              aria-pressed={selected}
              aria-label={t('onboarding.step3.colour_slot', {
                name: t(`onboarding.step3.colour_name.${slot}`),
              })}
              onClick={() => {
                if (taken) return;
                onChange(slot);
              }}
              className={cls}
            />
          );
        })}
      </div>
    </fieldset>
  );
}
